import React from "react";
import { toast } from "sonner";
import { Loader2, Search, X } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { fileUrl } from "@/lib/api";

/**
 * New Message — the member picker for Group Member Messaging V1 (spec §16).
 *
 * The search is the CANONICAL Group member directory (itself the People directory scoped
 * to this Group), so there is no parallel people search here and a non-member can never
 * appear. One selection opens the canonical 1:1; two or more create a multi-member
 * conversation, capped server-side at 25.
 */
export function GroupNewMessageDialog({ groupId, open, onOpenChange, onCreated,
                                        maxParticipants = 25, addMode = false,
                                        onPick }) {
  const [q, setQ] = React.useState("");
  const [dq, setDq] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [picked, setPicked] = React.useState([]);
  const [name, setName] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) { setQ(""); setDq(""); setPicked([]); setName(""); }
  }, [open]);
  React.useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  React.useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setLoading(true);
    api.get(`/groups/${groupId}/messages/eligible-members`,
      { params: { q: dq, limit: 20 } })
      .then(({ data }) => { if (live) setRows(data.members || []); })
      .catch(() => { if (live) setRows([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [open, groupId, dq]);

  const toggle = (person) => setPicked((prev) => (
    prev.find((p) => p.user_id === person.user_id)
      ? prev.filter((p) => p.user_id !== person.user_id)
      : prev.length + 1 >= maxParticipants
        ? (toast.error(`A conversation can have at most ${maxParticipants} people.`), prev)
        : [...prev, person]));

  const create = async () => {
    // In `addMode` the picker is reused to choose people to ADD to an existing
    // conversation: it hands the selection back so the caller can show the history
    // warning before anything is written (spec §10).
    if (addMode) {
      onOpenChange(false);
      onPick(picked.map((p) => p.user_id));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/messages/threads`, {
        participant_ids: picked.map((p) => p.user_id),
        name: picked.length > 1 ? name : null,
      });
      onOpenChange(false);
      onCreated(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start that conversation.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="group-new-message-dialog">
        <DialogHeader>
          <DialogTitle>{addMode ? "Add people" : "New message"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
                   placeholder="Search members of this group"
                   aria-label="Search members of this group"
                   data-testid="group-new-message-search" />
          </div>
          {picked.length ? (
            <div className="flex flex-wrap gap-1.5" data-testid="group-new-message-picked">
              {picked.map((p) => (
                <Badge key={p.user_id} variant="secondary" className="gap-1 pr-1">
                  {p.display_name}
                  <button type="button" onClick={() => toggle(p)}
                          aria-label={`Remove ${p.display_name}`}
                          data-testid={`group-new-message-remove-${p.user_id}`}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          ) : null}
          {picked.length > 1 && !addMode ? (
            <div className="space-y-1.5">
              <Label htmlFor="gm-name">Conversation name (optional)</Label>
              <Input id="gm-name" value={name} maxLength={80}
                     onChange={(e) => setName(e.target.value)}
                     placeholder="e.g. Quantum planning"
                     data-testid="group-new-message-name" />
            </div>
          ) : null}
          <div className="max-h-64 divide-y overflow-y-auto rounded-lg border"
               data-testid="group-new-message-results">
            {loading ? (
              <div className="flex justify-center p-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : rows.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">No members match.</p>
            ) : rows.map((p) => {
              const on = Boolean(picked.find((x) => x.user_id === p.user_id));
              return (
                <button key={p.user_id} type="button" onClick={() => toggle(p)}
                        aria-pressed={on}
                        className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-secondary ${on ? "bg-teal-50" : ""}`}
                        data-testid={`group-new-message-option-${p.user_id}`}>
                  <Avatar className="h-8 w-8">
                    {p.headshot_url ? <AvatarImage src={fileUrl(p.headshot_url)} alt="" /> : null}
                    <AvatarFallback className="text-[11px]">
                      {(p.display_name || "?")[0]}
                    </AvatarFallback>
                  </Avatar>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-[560]">{p.display_name}</span>
                    {p.headline ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {p.headline}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button disabled={busy || picked.length === 0} onClick={create}
                  data-testid="group-new-message-start">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {addMode ? "Continue"
              : picked.length > 1 ? "Start conversation" : "Start message"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
